import React, { useCallback } from 'react';
import {
  HeaderIcon,
  NaviIconHome,
  StackScreen,
  useStatusBarUpdate,
} from '@banhala/ui-kit';
import { useQueryClient } from '@tanstack/react-query';
import { Freeze } from 'react-freeze';
import {
  useHandleBackPressed,
  useInstanceId,
  useIsForegroundNow,
} from 'shared/src/hooks';
import { CartIcon, SearchIcon } from 'shared/src/component';
import { getNumberRange, isNotNil, useNavigator } from 'shared/src/utils';
import { AnalyticsScreen } from 'shared/src/context';
import type { ILogging } from 'shared/src/api/types/ILogging';
import { goodsService } from 'shared/src/api/goodsService';
import { GoodsDetailContents } from './components/GoodsDetailContents';
import { useLogClickHome } from './hooks/useLogClickHome';
import { useAffiliateAccessLog } from './hooks/useAffiliateAccessLog';
import { useTrackingLinkAccessLog } from './hooks/useTrackingLinkAccessLog';
import { BottomDockedHeightProvider } from 'shared/src/hooks/useBottomDockedHeight';
import { useGoodsSno } from './hooks/useGoodsSno';
import { useFixedCouponSno } from './hooks/useFixedCouponSno';
import { queryKey } from './constant/queryKey';
import { useEntryAdInfo } from './hooks/useEntryAdInfo';
import { useGoodsFrom } from './hooks/useGoodsFrom';
import { useStopSaleGuard } from './hooks/useStopSaleGuard';
import { useAtomValue } from 'jotai';
import { isPostGoodsActionBottomSheetOpenAtomFamily } from './atom/isPostGoodsActionBottomSheetOpen.atom';

type GoodsDetailData = Awaited<ReturnType<typeof goodsService.getGoodsDetail>>;
type GoodsBasicData = Awaited<ReturnType<typeof goodsService.getGoodsBasic>>;

const GoodsDetailPage: React.FC = () => {
  const { navigator } = useNavigator();
  // PostGoodsActionBottomSheet 가 떠있을 때는 hardware back 을 바텀시트(ui-kit
  // BottomSheetContainer 의 BackHandler) 로 propagation 시켜 바텀시트가 닫히도록 함.
  // brownfield 라 App/GoodsDetail 인스턴스가 여러 개 마운트될 수 있어
  // atomFamily(instanceId) 로 인스턴스 별 atom 분리. isForeground 가드는 안전 차원.
  const instanceId = useInstanceId();
  const isForeground = useIsForegroundNow();
  const isPostGoodsActionBottomSheetOpen = useAtomValue(
    isPostGoodsActionBottomSheetOpenAtomFamily(instanceId),
  );

  const { onPressBack, showBackButton } = useHandleBackPressed({
    shouldSkip: isForeground && isPostGoodsActionBottomSheetOpen,
  });

  const queryClient = useQueryClient();
  const goodsSno = useGoodsSno();
  const fixedCouponSno = useFixedCouponSno();
  // 상세 진입 출처 화면(FROM). 네이티브 상세와 달리 RN 상세는 자동 부착이 없어
  // getDefaults에 넣어 모든 이벤트(cart/like 포함)에 전파한다.
  const from = useGoodsFrom();

  useStopSaleGuard();
  useAffiliateAccessLog();
  useTrackingLinkAccessLog();
  const logClickHome = useLogClickHome();
  const entryAd = useEntryAdInfo();

  useStatusBarUpdate('white');

  const handleHomeClick = useCallback(() => {
    logClickHome();
    navigator.moveToHome();
  }, [logClickHome, navigator]);

  const getDefaults = useCallback((): ILogging | undefined => {
    const detail = queryClient.getQueryData<GoodsDetailData>(
      queryKey.goodsDetail(goodsSno, fixedCouponSno),
    );
    const basic = queryClient.getQueryData<GoodsBasicData>(
      queryKey.goodsBasic(goodsSno, fixedCouponSno),
    );
    if (!detail || !basic) return undefined;

    const categories = basic.display_categories?.[0] ?? null;

    return {
      analytics: {
        ...(from != null ? { FROM: from } : {}),
        CATEGORY_DEPTH: categories?.depth,
        CATEGORY_SNO: categories?.sno.toString(),
        CATEGORY_NAME: categories?.name,
        DELIVERY_LEAD_TIME:
          basic.delivery_type_info.delivery_type === 'standard'
            ? detail.goods.send_expectation_info?.days
            : null,
        DELIVERY_TYPE: basic.delivery_type_info.delivery_type,
        DISCOUNT_RATE: basic.price_info.discount_rate,
        GOODS_SNO: basic.sno.toString(),
        GOODS_NAME: basic.name,
        IS_NEW: basic.is_new ? '1' : '0',
        LIKES_COUNT: detail.goods.score.likes_count,
        LIKES_COUNT_RANGE: getNumberRange({
          rangeUnit: 500,
          target: detail.goods.score.likes_count,
        }),
        MARKET_SNO: basic.market.sno.toString(),
        MARKET_NAME: basic.market.name,
        MARKET_TYPE: basic.market.market_vertical_type.name,
        ORIGINAL_PRICE: basic.price_info.consumer,
        REVIEW_COUNT: detail.goods.score.total_review_count,
        REVIEW_COUNT_RANGE: getNumberRange({
          target: detail.goods.score.total_review_count,
          rangeUnit: 50,
        }),
        REVIEW_RATING: basic.positive_review_rate,
        SALES_PRICE: basic.price_info.thumbnail_price,
        SALES_PRICE_RANGE: getNumberRange({
          target: basic.price_info.thumbnail_price,
          rangeUnit: 5000,
        }),
        STANDARD_CATEGORY_SNO: basic.standard_category.sno.toString(),
        STANDARD_CATEGORY_NAME: basic.standard_category.name,
        IS_AD: isNotNil(entryAd) ? '1' : '0',
        ...detail.logging.analytics,
      },
    };
  }, [queryClient, goodsSno, fixedCouponSno, entryAd, from]);

  return (
    <AnalyticsScreen screen="GOODS_DETAIL" getDefaults={getDefaults}>
      <BottomDockedHeightProvider>
        <StackScreen
          hideBackButton={!showBackButton}
          onPressBack={onPressBack}
          backButtonTestID={'뒤로가기버튼'}
          rightIcons={[
            <HeaderIcon testID={'홈버튼'} onPress={handleHomeClick}>
              <NaviIconHome />
            </HeaderIcon>,
            <SearchIcon />,
            <CartIcon />,
          ]}
        >
          {/* brownfield: GoodsDetail 인스턴스가 여러 개 스택으로 쌓일 수 있고,
              모두 단일 RN 런타임(JS 스레드 1개)을 공유한다. 가려진 인스턴스가
              계속 렌더/effect 를 돌면 새 진입의 마운트가 그 뒤에 밀려 진입이
              느려진다. isForeground 가 false(가려짐) 면 Freeze 로 재조정을 멈춰
              JS 스레드를 비운다. (modal 자식이 떠있는 동안엔 useIsForegroundNow
              가 true 를 반환하므로 시트 뒤 화면은 얼지 않음) */}
          <Freeze freeze={!isForeground}>
            <GoodsDetailContents />
          </Freeze>
        </StackScreen>
      </BottomDockedHeightProvider>
    </AnalyticsScreen>
  );
};

export default GoodsDetailPage;
